import { AgentModel, AppApi } from '@/customApiClients/appApiClient/Api';

import { queueAgentSelectionPersist } from './agentSelectionGate';
import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import {
    AgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { useChatInteract } from '@chainlit/react-client';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import {
    availableModelsState,
    getHasAutoSelectedModel,
    getHasFetchedModels,
    getHasSyncedModel,
    selectedModelState,
    setHasAutoSelectedModel,
    setHasFetchedModels,
    setHasSyncedModel,
    setShouldPersistModel
} from '@/state/chat';

/**
 * Normal (non-managed) agent selector. Managed Agents are no longer shown
 * here — they live exclusively under the "Managed Agents" toggle in the
 * sidebar (composerMode === 'multiagent'), which opens MultiAgentSelector.
 */
export const ModelSelector = ({}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [models, setModels] = useRecoilState(availableModelsState);
    const [selectedModel, setSelectedModel] =
        useRecoilState(selectedModelState);

    const [settingAgent, setSettingAgent] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { windowMessage } = useChatInteract();

    const appApi = useMemo(
        () => new AppApi({ baseApiParams: { credentials: 'include' } }),
        []
    );

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    // Track current organization and reset flags when it changes
    const currentOrgIdRef = useRef<string | null>(null);

    useEffect(() => {
        const checkOrgChange = () => {
            const currentOrgId =
                localStorage.getItem('selected-organization-id') || 'default';

            // Initialize on first run
            if (currentOrgIdRef.current === null) {
                currentOrgIdRef.current = currentOrgId;
                return;
            }

            // Check if organization changed
            if (currentOrgIdRef.current !== currentOrgId) {
                console.log(
                    '🔄 Organization changed from',
                    currentOrgIdRef.current,
                    'to',
                    currentOrgId,
                    '- resetting model fetch flags'
                );
                // Reset model fetch flags to allow re-fetching for new organization
                setHasFetchedModels(false);
                setHasAutoSelectedModel(false);
                setHasSyncedModel(false);

                // Clear global state to force refetch
                setModels([]);

                // Update ref
                currentOrgIdRef.current = currentOrgId;

                // Trigger fetch for new org
                fetchModels();
            }
        };

        // Check on mount and when component becomes visible
        checkOrgChange();

        // Also check periodically (in case of navigation)
        const interval = setInterval(checkOrgChange, 1000);

        return () => clearInterval(interval);
    }, []);

    // Fetch available normal agents from mgmt API
    const fetchModels = useCallback(async () => {
        // Prevent concurrent fetches and double-fetching across all instances
        // Also check if we already have data (handles StrictMode remounting)
        if (getHasFetchedModels() || loadingModels || models.length > 0) return;
        setHasFetchedModels(true);

        setLoadingModels(true);
        setError(null);
        try {
            const agentsRes = await mgmtApi.api.v1AgentsList();
            const items = agentsRes?.data?.data || [];

            // Load model selection from global localStorage (not org-specific)
            const modelStorageKey = 'chainlit-selected-model';
            const savedModel = localStorage.getItem(modelStorageKey);
            if (
                savedModel &&
                savedModel !== 'undefined' &&
                savedModel !== '' &&
                !selectedModel
            ) {
                try {
                    const parsed = JSON.parse(savedModel);
                    if (parsed && typeof parsed === 'object' && parsed.model) {
                        // Validate the saved model exists in the fetched list
                        const matchingModel = items.find(
                            (m: AgentResponseModel) => m.model === parsed.model
                        );
                        if (matchingModel) {
                            setShouldPersistModel(false);
                            setSelectedModel(matchingModel);
                            setShouldPersistModel(true);
                        }
                    }
                } catch (e) {
                    console.warn(
                        'Failed to parse saved model from localStorage',
                        e
                    );
                }
            }

            setModels(items);
        } catch (e: any) {
            // On error, set fallback list
            setError(e?.message || 'Failed to load models');
            setHasFetchedModels(false); // Allow retry on error
        } finally {
            setLoadingModels(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial load: fetch models on mount
    useEffect(() => {
        fetchModels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync localStorage model selection to backend on mount (after data loads)
    // This only runs for models restored from localStorage, NOT for auto-selected models
    useEffect(() => {
        // Skip if model was auto-selected (it's already synced to backend by auto-selection)
        if (getHasAutoSelectedModel()) {
            return;
        }

        // Skip if already synced
        if (getHasSyncedModel()) {
            return;
        }

        // Wait for models to load
        if (!models.length || loadingModels) {
            return;
        }

        // Read directly from localStorage to check for saved model
        const rawLocalStorageData = localStorage.getItem(
            'chainlit-selected-model'
        );
        let savedModel: AgentResponseModel | null = null;
        if (
            rawLocalStorageData &&
            rawLocalStorageData !== 'undefined' &&
            rawLocalStorageData !== 'null'
        ) {
            try {
                const parsed = JSON.parse(rawLocalStorageData);
                if (parsed && typeof parsed === 'object' && parsed.model) {
                    savedModel = parsed;
                }
            } catch {
                // Invalid JSON, ignore
            }
        }

        // If no saved model in localStorage, let auto-selection handle it
        if (!savedModel) {
            return;
        }

        // Validate that the saved model exists in available models (compare by model name, not guid)
        const matchingModel = models.find((m) => m.model === savedModel!.model);

        if (matchingModel) {
            // If we have a model in localStorage, sync it to backend
            // Use the matching model from current org (in case guid differs)
            setAgentForUser(matchingModel, true);
            setSelectedModel(matchingModel); // Update to use current org's version
            setHasSyncedModel(true);
        } else {
            // Model doesn't exist in current org - DON'T clear localStorage!
            // The model might exist in another org, so keep it saved globally.
            // Just let auto-selection pick one for this org.
            setHasSyncedModel(true); // Mark as synced so this effect doesn't re-run
            // Auto-selection will handle picking a model
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models.length, loadingModels]);

    // Auto-select first model if none is selected
    useEffect(() => {
        // Skip if already auto-selected, loading, or no models available
        if (!models.length || loadingModels || getHasAutoSelectedModel()) {
            return;
        }

        // Check if the currently selected model still exists in the available models (by model name)
        const selectedModelStillExists =
            selectedModel &&
            models.some((m) => m.model === selectedModel.model);

        // Check if we had a model selected but it was removed
        const hadModel = selectedModel !== undefined;
        const modelWasRemoved = hadModel && !selectedModelStillExists;

        // Validate localStorage data - must be valid JSON object, not "undefined" string
        const rawLocalStorageData = localStorage.getItem(
            'chainlit-selected-model'
        );
        let hasValidLocalStorageData = false;
        if (
            rawLocalStorageData &&
            rawLocalStorageData !== 'undefined' &&
            rawLocalStorageData !== 'null'
        ) {
            try {
                const parsed = JSON.parse(rawLocalStorageData);
                hasValidLocalStorageData =
                    parsed && typeof parsed === 'object' && parsed.model;
            } catch {
                hasValidLocalStorageData = false;
            }
        }

        // Only auto-select if:
        // 1. No model is currently selected OR selected model was removed from backend
        // 2. No VALID model was saved in localStorage OR the saved model was removed
        // 3. Models are available
        const shouldAutoSelect =
            (!selectedModel || modelWasRemoved) &&
            (!hasValidLocalStorageData || modelWasRemoved) &&
            models.length > 0;

        if (shouldAutoSelect) {
            const firstModel = models[0];
            setAgentForUser(firstModel, false); // Don't persist auto-selection to localStorage
            setHasAutoSelectedModel(true);
        } else if (modelWasRemoved) {
            // If selected model was removed, clear the local selection
            setSelectedModel(undefined);
            setHasAutoSelectedModel(true);
        } else {
            // Mark as done even if we didn't auto-select (e.g., valid localStorage exists)
            setHasAutoSelectedModel(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models, selectedModel, loadingModels]);

    // Set agent configuration for user
    // persist: if false, don't save to localStorage (used for auto-selection)
    const setAgentForUser = async (
        modelData: AgentResponseModel,
        persist: boolean = true
    ) => {
        setSettingAgent(true);
        setError(null);

        try {
            const agentModel: AgentModel = {
                Name: modelData.displayName,
                Model: modelData.model,
                ModelCompany: modelData.modelCompany,
                Organization: modelData.organization,
                ApiKey: modelData.apiKey,
                Description: modelData.description
            };

            // Embedded report sessions must NEVER call setAgentOnUser — it writes
            // to singleton_agent_user_selection, which is keyed by user_key only
            // (not per-tab/session), and would silently overwrite the same
            // logged-in user's Managed Agent choice in their standalone AI tab.
            // A thread-scoped REST persist doesn't work either: the thread often
            // doesn't exist client-side yet at pick time. Instead, push the
            // override straight over the live socket (the session always exists)
            // via windowMessage — see on_window_message's "report_companion_agent"
            // handler in chainlit_start.py, which stashes it on cl.user_session
            // for chat_entry.setup_runnable to prefer on the next message.
            if (isEmbeddedSidebar) {
                windowMessage({ type: 'report_companion_agent', agent: agentModel });
                setSelectedModel(modelData);
                return;
            }

            const res = await queueAgentSelectionPersist(() =>
                appApi.agent.setAgentOnUser({ agent: agentModel })
            );

            if (res?.data?.status === 'ok') {
                setShouldPersistModel(persist);
                setSelectedModel(modelData);
                if (persist) {
                    setShouldPersistModel(true);
                }
            }
        } catch (e: any) {
            setError(
                e?.message || 'An error occurred while setting the agent.'
            );
            console.error('Failed to set agent: ', e);
        } finally {
            setSettingAgent(false);
        }
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="gap-2 w-full min-w-0 max-w-full text-sm"
                    disabled={settingAgent}
                >
                    {settingAgent ? (
                        <Loader2 className="animate-spin" />
                    ) : (
                        <span className="block flex-1 min-w-0 font-medium text-left truncate">
                            {!loadingModels && models.length === 0
                                ? 'No models available'
                                : selectedModel?.displayName || 'Select Model'}
                        </span>
                    )}
                    <ChevronDown
                        className={`transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                    />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="bg-background w-[280px]"
            >
                {loadingModels && (
                    <DropdownMenuItem disabled>
                        <Loader2 className="mr-2 animate-spin" />
                        Loading models...
                    </DropdownMenuItem>
                )}
                {!loadingModels && models.length === 0 && (
                    <DropdownMenuItem disabled>
                        No models available
                    </DropdownMenuItem>
                )}

                <DropdownMenuGroup>
                    {models.map((model) => {
                        const isSelected = selectedModel?.guid === model.guid;

                        return (
                            <DropdownMenuItem
                                key={model.guid}
                                onClick={() => setAgentForUser(model, true)}
                                disabled={settingAgent}
                                className="justify-between"
                            >
                                <div className="flex flex-col">
                                    <span>
                                        {model.displayName}
                                    </span>
                                    {model.description && (
                                        <span className="text-muted-foreground text-xs">
                                            {model.description}
                                        </span>
                                    )}
                                </div>
                                <Check
                                    className={`size-4! ${
                                        isSelected ? 'opacity-100' : 'opacity-0'
                                    }`}
                                />
                            </DropdownMenuItem>
                        );
                    })}

                    {error && (
                        <>
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5 text-destructive text-xs">
                                {error}
                            </div>
                        </>
                    )}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ModelSelector;
